import { Card } from '@/components/UI';
import ScorecardTargetProvenancePanel from './ScorecardTargetProvenancePanel';
import type { ScorecardPreviewDiagnostics } from './types';

interface PreviewDiagnosticsProps {
    diagnostics: ScorecardPreviewDiagnostics;
}

const PreviewDiagnostics = ({ diagnostics }: PreviewDiagnosticsProps) => {
    return (
        <Card className="p-3">
            <div className="text-sm text-zinc-300">
                {diagnostics.entryIdentityDrift && (
                    <div className="mb-3 rounded border border-amber-700/60 bg-amber-950/30 p-3 text-amber-100">
                        This strategy&apos;s current entry population differs from the scorecard&apos;s training identity. Treat this
                        preview as diagnostic only; prepare a compatible scorecard version before testing or attaching it.
                    </div>
                )}
                <div>
                    Duration used: <span className="text-white font-medium">{diagnostics.durationUsed ?? '—'}</span>
                </div>
                {diagnostics.optionReturnDefinition && !diagnostics.targetProvenance && (
                    <div className="mt-1">
                        Return definition: <span className="text-white font-medium">{diagnostics.optionReturnDefinition.label}</span>
                        <span className="text-zinc-500">
                            {' '}
                            (BTO denominator {diagnostics.optionReturnDefinition.buyToOpenDenominator}; STO denominator{' '}
                            {diagnostics.optionReturnDefinition.sellToOpenDenominator})
                        </span>
                    </div>
                )}
                <div className="mt-1">
                    priceAction enabled:{' '}
                    <span className="text-white font-medium">{diagnostics.requirements?.states?.priceAction ? 'true' : 'false'}</span>,
                    keyLevels enabled:{' '}
                    <span className="text-white font-medium">{diagnostics.requirements?.entityData?.keyLevels ? 'true' : 'false'}</span>
                </div>
                <div className="mt-1 text-zinc-400">
                    Bars: entity duration {diagnostics.requirements?.bars?.entity?.duration ?? '—'}; daily{' '}
                    {String(diagnostics.requirements?.bars?.entity?.daily ?? false)}; bars1m{' '}
                    {String(diagnostics.requirements?.bars?.entity?.bars1m ?? false)}
                    {diagnostics.requirements?.bars?.entity?.additionalDurations?.length
                        ? `; additional ${diagnostics.requirements.bars.entity.additionalDurations.join(', ')}`
                        : ''}
                </div>
                {diagnostics.targetProvenance && (
                    <div className="mt-3">
                        <ScorecardTargetProvenancePanel provenance={diagnostics.targetProvenance} />
                    </div>
                )}
                {diagnostics.counts && (
                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-zinc-400 md:grid-cols-4">
                        <div>
                            Total entries: <span className="text-white font-medium">{diagnostics.counts.totalEntries ?? '—'}</span>
                        </div>
                        <div>
                            Returned: <span className="text-white font-medium">{diagnostics.counts.returned ?? '—'}</span>
                            {diagnostics.counts.truncated ? <span className="ml-1 text-amber-300">(truncated)</span> : null}
                        </div>
                        <div>
                            Accepted: <span className="text-green-400 font-medium">{diagnostics.counts.acceptedEntries ?? '—'}</span>
                        </div>
                        <div>
                            Rejected: <span className="text-red-400 font-medium">{diagnostics.counts.rejectedEntries ?? '—'}</span>
                        </div>
                        <div>
                            Enabled rules: <span className="text-white font-medium">{diagnostics.counts.enabledRuleCount ?? '—'}</span>
                        </div>
                        <div>
                            Scored entries: <span className="text-white font-medium">{diagnostics.counts.rustScoredEntries ?? '—'}</span>
                        </div>
                        <div className="md:col-span-2">
                            Entries without eligible baseline:{' '}
                            <span
                                className={
                                    diagnostics.counts.entityNotInExperimentEntries
                                        ? 'text-amber-300 font-medium'
                                        : 'text-white font-medium'
                                }
                            >
                                {diagnostics.counts.entityNotInExperimentEntries ?? 0}
                            </span>
                        </div>
                        <div className="md:col-span-2">
                            Walk-forward / OOS entries:{' '}
                            <span
                                className={
                                    diagnostics.counts.beyondTrainingCutoffEntries ? 'text-amber-300 font-medium' : 'text-white font-medium'
                                }
                            >
                                {diagnostics.counts.beyondTrainingCutoffEntries ?? 0}
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </Card>
    );
};

export default PreviewDiagnostics;
